import type { SuCategory, SuOwnerKey, SuStatusKey, SuTask } from '@elva/ui';
import type { IconKind } from '@elva/ui';
import { growthGet, growthPost, growthPostAsync } from '@/data/http';
import { setupOwnerToFe } from '@/data/growthEnums';
import { enumLower, num, str } from '@/data/adapt';

interface BeSetupTask {
  task_key?: string;
  id?: string | number;
  title?: string;
  status?: string;
  owner?: string;
  detail?: string;
  category?: string;
  payload?: Record<string, unknown>;
  completed_at?: string | null;
}

interface BeSetupCategory {
  id?: string;
  category_key?: string;
  title?: string;
  description?: string;
  icon?: string;
  tasks?: BeSetupTask[];
}

interface BePhaseSummary {
  required_count?: number;
  done_count?: number;
  progress_pct?: number;
}

interface BeSetupChecklist {
  categories?: BeSetupCategory[];
  items?: BeSetupTask[];
  tasks?: BeSetupTask[];
  phase_summaries?: Record<string, BePhaseSummary>;
  meta?: {
    has_more?: boolean;
    page?: number;
    page_size?: number;
    total_count?: number;
  };
}

export interface SetupChecklistData {
  categories: SuCategory[];
  /** True when backend returns zero items (unseeded clinic — do not show 100% complete). */
  isUnseeded: boolean;
}

type TaskCatalogEntry = {
  title: string;
  categoryId: string;
  phaseId?: string;
  owner?: SuOwnerKey;
  detail?: string;
  icon?: IconKind;
};

const PHASE_ORDER = ['foundation', 'campaigns', 'content', 'go_live'] as const;

const PHASE_CATALOG: Record<string, { title: string; description: string; icon: IconKind }> = {
  foundation: {
    title: 'Foundation',
    description: 'Connect accounts and configure core settings',
    icon: 'target',
  },
  campaigns: {
    title: 'Campaigns',
    description: 'Launch and configure paid search campaigns',
    icon: 'broadcast',
  },
  content: {
    title: 'Content',
    description: 'Articles, landing pages, and GBP content',
    icon: 'doc',
  },
  go_live: {
    title: 'Go live',
    description: 'Final checks before your module goes live',
    icon: 'check-circ',
  },
};

/** Backend task_key → UI catalog metadata (title, phase, owner, detail). */
const TASK_CATALOG: Record<string, TaskCatalogEntry> = {
  connect_google_ads: {
    title: 'Connect Google Ads account',
    categoryId: 'google_ads',
    phaseId: 'foundation',
    owner: 'dentist',
    detail: 'Link your Google Ads account so ELVA can manage campaigns',
  },
  set_target_area: {
    title: 'Set target service area',
    categoryId: 'google_ads',
    phaseId: 'foundation',
    owner: 'dentist',
    detail: 'Define your ad radius and excluded zip codes',
  },
  google_ads_linked: {
    title: 'Google Ads account linked',
    categoryId: 'google_ads',
    owner: 'elva',
  },
  billing_attached: {
    title: 'Billing account attached',
    categoryId: 'google_ads',
    owner: 'dentist',
  },
  conversion_tracking: {
    title: 'Conversion tracking configured',
    categoryId: 'google_ads',
    owner: 'elva',
  },
  initial_campaigns: {
    title: 'Initial campaign structure built',
    categoryId: 'google_ads',
    owner: 'elva',
  },
  cname_dns: {
    title: 'CNAME DNS record added',
    categoryId: 'landing_pages',
    owner: 'webmaster',
  },
  ssl_provisioned: {
    title: 'SSL certificate provisioned',
    categoryId: 'landing_pages',
    owner: 'elva',
  },
  first_landing_page: {
    title: 'First landing page generated',
    categoryId: 'landing_pages',
    owner: 'elva',
  },
  gbp_connected: {
    title: 'Google Business Profile connected',
    categoryId: 'gbp',
    owner: 'elva',
  },
  call_tracking_pool: {
    title: 'Call tracking number pool provisioned',
    categoryId: 'call_tracking',
    owner: 'elva',
  },
  pms_integration: {
    title: 'PMS integration active',
    categoryId: 'integrations',
    owner: 'elva',
  },
};

const CATEGORY_TO_PHASE: Record<string, string> = {
  google_ads: 'foundation',
  landing_pages: 'content',
  gbp: 'foundation',
  call_tracking: 'campaigns',
  integrations: 'go_live',
};

const CATEGORY_CATALOG: Record<string, { title: string; description: string; icon: IconKind }> = {
  google_ads: {
    title: 'Google Ads',
    description: 'Connection, billing, and conversion tracking',
    icon: 'target',
  },
  landing_pages: {
    title: 'Landing Pages',
    description: 'Custom subdomain and hosting',
    icon: 'upload',
  },
  gbp: {
    title: 'Google Business Profile',
    description: 'Profile connection and verification',
    icon: 'pin',
  },
  call_tracking: {
    title: 'Call Tracking',
    description: 'Dynamic number insertion and attribution',
    icon: 'phone',
  },
  integrations: {
    title: 'Integrations',
    description: 'PMS and analytics connections',
    icon: 'bolt',
  },
};

function humanizeTaskKey(key: string): string {
  return key
    .split('_')
    .filter(Boolean)
    .map((word) => word.charAt(0).toUpperCase() + word.slice(1))
    .join(' ');
}

function resolvePhaseId(catalog?: TaskCatalogEntry, categoryId?: string): string {
  if (catalog?.phaseId) return catalog.phaseId;
  if (catalog?.categoryId) return CATEGORY_TO_PHASE[catalog.categoryId] ?? 'foundation';
  if (categoryId) return CATEGORY_TO_PHASE[categoryId] ?? 'foundation';
  return 'foundation';
}

function adaptOwner(value: string | undefined, catalogOwner?: SuOwnerKey): SuOwnerKey {
  const fe = setupOwnerToFe(value);
  if (fe === 'dentist' || fe === 'you') return 'dentist';
  if (fe === 'elva') return 'elva';
  if (catalogOwner) return catalogOwner;
  return 'webmaster';
}

function adaptStatus(value: string | undefined): SuStatusKey {
  const v = enumLower(value, 'not_started');
  const allowed: SuStatusKey[] = [
    'done',
    'in_progress',
    'waiting_on_you',
    'waiting_on_webmaster',
    'not_started',
    'broken',
  ];
  return allowed.includes(v as SuStatusKey) ? (v as SuStatusKey) : 'not_started';
}

function adaptTask(row: BeSetupTask, index: number, taskKey?: string): SuTask {
  const key = taskKey ?? str(row.task_key ?? row.id, `task-${index}`);
  const catalog = TASK_CATALOG[key];
  return {
    id: typeof row.id === 'number' ? row.id : num(row.id, index + 1),
    taskKey: key,
    title: str(row.title ?? catalog?.title, humanizeTaskKey(key)),
    status: adaptStatus(row.status),
    owner: adaptOwner(row.owner, catalog?.owner),
    detail: str(row.detail ?? catalog?.detail, '—'),
  };
}

function resolvePhaseFromRow(row: BeSetupTask, catalog?: TaskCatalogEntry): string {
  const payloadPhase = row.payload?.phase;
  if (typeof payloadPhase === 'string' && payloadPhase) return payloadPhase;
  const categoryId = str(row.category ?? catalog?.categoryId, 'google_ads');
  return resolvePhaseId(catalog, categoryId);
}

function groupTasksByPhase(rows: BeSetupTask[]): Map<string, SuTask[]> {
  const byPhase = new Map<string, SuTask[]>();
  for (const [i, row] of rows.entries()) {
    const key = str(row.task_key ?? row.id, `task-${i}`);
    const catalog = TASK_CATALOG[key];
    const phaseId = resolvePhaseFromRow(row, catalog);
    const list = byPhase.get(phaseId) ?? [];
    list.push(adaptTask(row, i, key));
    byPhase.set(phaseId, list);
  }
  return byPhase;
}

function groupTasksByCategory(rows: BeSetupTask[]): Map<string, SuTask[]> {
  const byCategory = new Map<string, SuTask[]>();
  for (const [i, row] of rows.entries()) {
    const key = str(row.task_key ?? row.id, `task-${i}`);
    const catalog = TASK_CATALOG[key];
    const categoryId = str(row.category ?? catalog?.categoryId, 'google_ads');
    const list = byCategory.get(categoryId) ?? [];
    list.push(adaptTask(row, i, key));
    byCategory.set(categoryId, list);
  }
  return byCategory;
}

export function adaptSetupChecklist(be: BeSetupChecklist): SetupChecklistData {
  if (be.categories?.length) {
    return {
      isUnseeded: false,
      categories: be.categories.map((cat, catIndex) => {
        const catKey = str(cat.id ?? cat.category_key, `cat-${catIndex}`);
        const meta = CATEGORY_CATALOG[catKey];
        return {
          id: catKey,
          title: str(cat.title ?? meta?.title, catKey),
          description: str(cat.description ?? meta?.description, ''),
          icon: (str(cat.icon ?? meta?.icon, 'bolt') as IconKind) ?? 'bolt',
          tasks: (cat.tasks ?? []).map((task, i) => adaptTask({ ...task, category: catKey }, i)),
        };
      }),
    };
  }

  const rows = be.items ?? be.tasks ?? [];
  if (rows.length === 0) {
    return { categories: [], isUnseeded: true };
  }

  const usePhases = Boolean(be.phase_summaries);
  if (usePhases) {
    const byPhase = groupTasksByPhase(rows);
    const categories: SuCategory[] = [];
    for (const phaseId of PHASE_ORDER) {
      const tasks = byPhase.get(phaseId) ?? [];
      if (tasks.length === 0) continue;
      const meta = PHASE_CATALOG[phaseId];
      categories.push({
        id: phaseId,
        title: meta?.title ?? humanizeTaskKey(phaseId),
        description: meta?.description ?? '',
        icon: meta?.icon ?? 'bolt',
        tasks,
      });
    }
    for (const [phaseId, tasks] of byPhase.entries()) {
      if (PHASE_ORDER.includes(phaseId as (typeof PHASE_ORDER)[number])) continue;
      categories.push({
        id: phaseId,
        title: humanizeTaskKey(phaseId),
        description: '',
        icon: 'bolt',
        tasks,
      });
    }
    return { categories, isUnseeded: false };
  }

  const byCategory = groupTasksByCategory(rows);
  return {
    isUnseeded: false,
    categories: [...byCategory.entries()].map(([categoryId, tasks]) => {
      const meta = CATEGORY_CATALOG[categoryId];
      return {
        id: categoryId,
        title: meta?.title ?? humanizeTaskKey(categoryId),
        description: meta?.description ?? '',
        icon: meta?.icon ?? 'bolt',
        tasks,
      };
    }),
  };
}

async function fetchSetupChecklistPage(page: number, limit: number): Promise<BeSetupChecklist> {
  return growthGet<BeSetupChecklist>('/setup-checklist/', { page, limit });
}

export async function fetchSetupChecklist(): Promise<SetupChecklistData> {
  const limit = 100;
  let page = 1;
  const allItems: BeSetupTask[] = [];
  let phaseSummaries: Record<string, BePhaseSummary> | undefined;
  let hasMore = true;

  while (hasMore && page <= 20) {
    const be = await fetchSetupChecklistPage(page, limit).catch(() => undefined);
    if (!be) break;

    const batch = be.items ?? be.tasks ?? [];
    allItems.push(...batch);
    phaseSummaries = be.phase_summaries ?? phaseSummaries;

    const meta = be.meta;
    hasMore = meta?.has_more === true || (meta?.has_more == null && batch.length >= limit);
    page += 1;
  }

  return adaptSetupChecklist({
    items: allItems,
    ...(phaseSummaries ? { phase_summaries: phaseSummaries } : {}),
  });
}

export async function markSetupTaskDone(taskKey: string) {
  return growthPost(`/setup-checklist/${encodeURIComponent(taskKey)}/mark-done/`);
}

export async function skipSetupTask(taskKey: string) {
  return growthPost(`/setup-checklist/${encodeURIComponent(taskKey)}/skip/`, {
    confirm_required: true,
  });
}

export async function contactSetupWebmaster(message: string) {
  return growthPostAsync('/setup-checklist/contact-webmaster/', { message });
}
